import { getCounties } from '@/shared/api/geographyApi';

// Shared by every useLoadScript call so the Google script loads once with one config.
export const GOOGLE_MAPS_LIBRARIES: 'places'[] = ['places'];

export type LocationData = {
  address: string;
  latitude: number;
  longitude: number;
  countyId?: string;
  stateCode?: string;
};

/**
 * Turns a Google geocoder result into the location filter the search uses:
 * the US county (matched by name in our reference data) and state.
 * Used by the location box and by `?city=` links, so both resolve the same way.
 */
export const toLocationData = async (result: google.maps.GeocoderResult): Promise<LocationData> => {
  let countyName: string | undefined;
  let stateCode: string | undefined;
  for (const component of result.address_components || []) {
    // administrative_area_level_2 is county in the US
    if (component.types.includes('administrative_area_level_2')) countyName = component.long_name;
    // administrative_area_level_1 is state
    if (component.types.includes('administrative_area_level_1')) stateCode = component.short_name;
  }

  let countyId: string | undefined;
  if (countyName && stateCode) {
    try {
      const counties = await getCounties(stateCode);
      countyId = counties.find((c) => c.name === countyName && c.stateCode === stateCode)?.id;
    } catch (error) {
      console.error('Failed to look up county:', error);
    }
  }

  return {
    address: result.formatted_address,
    latitude: result.geometry.location.lat(),
    longitude: result.geometry.location.lng(),
    ...(countyId !== undefined && { countyId }),
    ...(stateCode !== undefined && { stateCode }),
  };
};

/**
 * Geocodes free text (e.g. "Denver, CO") restricted to the US. Requires the
 * Google Maps script to be loaded. Returns null when nothing matches.
 */
export const geocodeUsLocation = async (address: string): Promise<LocationData | null> => {
  try {
    const { results } = await new window.google.maps.Geocoder().geocode({
      address,
      componentRestrictions: { country: 'US' },
    });
    return results[0] ? await toLocationData(results[0]) : null;
  } catch {
    // The geocoder rejects on ZERO_RESULTS as well as on real failures.
    return null;
  }
};
